import { Alert } from "antd";
import { useEffect } from "react";
import type { ConsoleRoute } from "@/router/routes";
import { defaultTheme } from "@/runtime/config";
import { UserMessagePage } from "@/pages/userMessage/UserMessagePage";
import { NotificationDetailPage } from "@/pages/userMessage/NotificationDetailPage";
import { RegistrationDetailPage, RegistrationListPage } from "@/pages/registration/RegistrationPages";
import { PersonalInfoPage } from "@/pages/me/PersonalInfoPage";
import { SafeSettingPage } from "@/pages/me/SafeSettingPage";
import { UpdatePasswordPage } from "@/pages/me/UpdatePasswordPage";
import { UpdateEmailPage } from "@/pages/me/UpdateEmailPage";
import { AuthenticationPage } from "@/pages/me/AuthenticationPage";
import { PhoneAuthenticationPage } from "@/pages/me/PhoneAuthenticationPage";
import { SubAccountManagementPage } from "@/pages/me/SubAccountManagementPage";
import { SubAccountDetailPage } from "@/pages/me/SubAccountDetailPage";
import { SubAccountEditPage } from "@/pages/me/SubAccountEditPage";
import { InvitationHistoryPage } from "@/pages/me/InvitationHistoryPage";
import { InvitationRecordPage } from "@/pages/me/InvitationRecordPage";
import { SmsManagementPage } from "@/pages/me/SmsManagementPage";
import { SmsManagementEditPage } from "@/pages/me/SmsManagementEditPage";
import { InvoiceListPage } from "@/pages/me/InvoiceListPage";
import { InvoiceFormPage } from "@/pages/me/InvoiceFormPage";
import { AddressListPage } from "@/pages/me/AddressListPage";
import { AddressFormPage } from "@/pages/me/AddressFormPage";
import { BankAccountListPage } from "@/pages/me/BankAccountListPage";
import { BankAccountFormPage } from "@/pages/me/BankAccountFormPage";
import { MemberPage } from "@/pages/me/MemberPage";
import { FootprintsPage } from "@/pages/follow/FootprintsPage";
import { FavoritePage } from "@/pages/follow/FavoritePage";
import { InterestPage } from "@/pages/follow/InterestPage";
import { PriceWarningPage } from "@/pages/follow/PriceWarningPage";
import { MyOrderPage } from "@/pages/order/MyOrderPage";
import { OrderDetailPage } from "@/pages/order/OrderDetailPage";
import { OrderConfirmPage } from "@/pages/order/OrderConfirmPage";
import { DeliveryDetailPage } from "@/pages/order/DeliveryDetailPage";
import { MyInvoicePage } from "@/pages/order/MyInvoicePage";
import { InvoiceDetailPage } from "@/pages/order/InvoiceDetailPage";
import { IntendDetailPage, IntendListPage } from "@/pages/console/IntendPages";
import { ProjectDetailPage, ProjectListPage } from "@/pages/console/ProjectPages";
import { ContractDetailPage, ContractListPage } from "@/pages/console/ContractPages";
import { PurchaseInvoiceDetailPage, PurchaseInvoiceFormPage, PurchaseInvoiceListPage } from "@/pages/console/PurchaseInvoicePages";
import { PaymentDetailPage, PaymentFormPage, PaymentListPage } from "@/pages/console/PaymentPages";
import { ConsoleProductPage, PurchaseGoodsDetailPage, PurchaseGoodsFormPage, PurchaseGoodsListPage } from "@/pages/console/PurchaseGoodsPages";
import { ReceiptDetailPage, ReceiptFormPage, ReceiptListPage } from "@/pages/console/ReceiptPages";
import { ReconciliationDetailPage, ReconciliationFormPage, ReconciliationListPage } from "@/pages/console/ReconciliationPages";
import { ShipInfoServiceFeeDetailPage, ShipInfoServiceFeeListPage } from "@/pages/logistics/ShipInfoServiceFeePages";
import { ShipOrderDetailPage, ShipOrderFormPage, ShipOrderListPage } from "@/pages/logistics/ShipOrderPages";
import { FindLocationPage, TruckLocationPage } from "@/pages/logistics/LocationPages";

/** 业务页面迁移前的明确占位，禁止伪装成已完成页面。 */
export function RoutePage({ route }: { route: ConsoleRoute }) {
    useEffect(() => {
        document.title = route.title ? `${route.title.trim()} - ${defaultTheme.systemName}` : defaultTheme.systemName;
    }, [route.title]);
    if (route.name === "UserMessageIndex") return <UserMessagePage />;
    if (route.name === "UserMessageNotificationDetail") return <NotificationDetailPage />;
    if (route.name === "MyRegistration") return <RegistrationListPage />;
    if (route.name === "MyRegistrationDetail") return <RegistrationDetailPage />;
    if (route.name === "Info") return <PersonalInfoPage />;
    if (route.name === "SafeSetting") return <SafeSettingPage />;
    if (route.name === "UpdatePassword") return <UpdatePasswordPage />;
    if (route.name === "UpdateEmail") return <UpdateEmailPage />;
    if (route.name === "Authentication") return <AuthenticationPage />;
    if (route.name === "AuthenticationPhone") return <PhoneAuthenticationPage />;
    if (route.name === "SubAccountManagement") return <SubAccountManagementPage />;
    if (route.name === "SubAccountManagementDetail") return <SubAccountDetailPage />;
    if (route.name === "SubAccountManagementEdit") return <SubAccountEditPage />;
    if (route.name === "InvitationHistory") return <InvitationHistoryPage />;
    if (route.name === "InvitationRecord") return <InvitationRecordPage />;
    if (route.name === "SMSManagement") return <SmsManagementPage />;
    if (route.name === "SMSManagementEdit") return <SmsManagementEditPage />;
    if (route.name === "Invoice") return <InvoiceListPage />;
    if (route.name === "InvoiceAdd") return <InvoiceFormPage />;
    if (route.name === "Address") return <AddressListPage />;
    if (route.name === "AddressAdd") return <AddressFormPage />;
    if (route.name === "BankAccount") return <BankAccountListPage />;
    if (route.name === "BankAccountAdd") return <BankAccountFormPage />;
    if (route.name === "Member") return <MemberPage />;
    if (route.name === "Footprints") return <FootprintsPage />;
    if (route.name === "Favorite") return <FavoritePage />;
    if (route.name === "Interest") return <InterestPage />;
    if (route.name === "PriceWarning") return <PriceWarningPage />;
    if (route.name === "MyOrder") return <MyOrderPage />;
    if (route.name === "OrderDetail") return <OrderDetailPage />;
    if (route.name === "OrderConfirm") return <OrderConfirmPage />;
    if (route.name === "DeliveryDetail") return <DeliveryDetailPage />;
    if (route.name === "MyInvoice") return <MyInvoicePage />;
    if (route.name === "InvoiceDetail") return <InvoiceDetailPage />;
    if (route.name === "ConsoleIntend") return <IntendListPage />;
    if (route.name === "ConsoleIntendDetail") return <IntendDetailPage />;
    if (route.name === "ConsoleProject") return <ProjectListPage />;
    if (route.name === "ConsoleProjectDetail") return <ProjectDetailPage />;
    if (route.name === "ConsoleContract") return <ContractListPage />;
    if (route.name === "ConsoleContractDetail") return <ContractDetailPage />;
    if (route.name === "ConsoleInvoice") return <PurchaseInvoiceListPage />;
    if (route.name === "ConsoleInvoiceForm") return <PurchaseInvoiceFormPage />;
    if (route.name === "ConsoleInvoiceDetail") return <PurchaseInvoiceDetailPage />;
    if (route.name === "ConsolePayment") return <PaymentListPage />;
    if (route.name === "ConsolePaymentAdd") return <PaymentFormPage />;
    if (route.name === "ConsolePaymentDetail") return <PaymentDetailPage />;
    if (route.name === "ConsolePurchaseGoods") return <PurchaseGoodsListPage />;
    if (route.name === "ConsolePurchaseGoodsAdd") return <PurchaseGoodsFormPage />;
    if (route.name === "ConsolePurchaseGoodsDetail") return <PurchaseGoodsDetailPage />;
    if (route.name === "ConsoleProduct") return <ConsoleProductPage />;
    if (route.name === "ConsoleReceipt") return <ReceiptListPage />;
    if (route.name === "ConsoleReceiptAdd" || route.name === "ConsoleReceiptEdit") return <ReceiptFormPage />;
    if (route.name === "ConsoleReceiptDetail") return <ReceiptDetailPage />;
    if (route.name === "ConsoleReconciliation") return <ReconciliationListPage />;
    if (route.name === "ConsoleReconciliationForm") return <ReconciliationFormPage />;
    if (route.name === "ConsoleReconciliationDetail") return <ReconciliationDetailPage />;
    if (route.name === "ShipInfoServiceFee") return <ShipInfoServiceFeeListPage />;
    if (route.name === "ShipInfoServiceFeeDetail") return <ShipInfoServiceFeeDetailPage />;
    if (route.name === "ShipOrder") return <ShipOrderListPage />;
    if (route.name === "ShipOrderEdit") return <ShipOrderFormPage />;
    if (route.name === "ShipOrderDetail") return <ShipOrderDetailPage />;
    if (route.name === "FindLocation") return <FindLocationPage />;
    if (route.name === "TruckLocation") return <TruckLocationPage />;
    return <section className="route-placeholder" data-route-name={route.name}>
        <h1>{route.title}</h1>
        <Alert showIcon type="info" message={`${route.domain}页面已登记，业务实现将在对应页面任务完成`} />
    </section>;
}
